import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

const PROTO_VERSION = 1
const MSG_DATA = 0x01
const MSG_CRED_PROMPT = 0x04
const MSG_CRED_REPLY = 0x05
const MSG_CONTROL = 0x06

function encodeFrame(type: number, payload: Uint8Array): Uint8Array {
  const buf = new Uint8Array(2 + payload.byteLength)
  buf[0] = PROTO_VERSION
  buf[1] = type
  buf.set(payload, 2)
  const frame = new Uint8Array(4 + buf.byteLength)
  const view = new DataView(frame.buffer)
  view.setUint32(0, buf.byteLength)
  frame.set(buf, 4)
  return frame
}

function encodeJSON(type: number, obj: object): Uint8Array {
  const enc = new TextEncoder()
  return encodeFrame(type, enc.encode(JSON.stringify(obj)))
}

type Status = 'connecting' | 'connected' | 'disconnected' | 'denied'

interface FrameMeta {
  fmt: string
  x?: number
  y?: number
  w?: number
  h?: number
  seq?: number
}

type FrameJob = { meta: FrameMeta; payload: Uint8Array }

/** Wait this long for a missing seq before skipping the gap. */
const GAP_SKIP_MS = 500
/** Gaps larger than this skip immediately instead of waiting. */
const MAX_GAP_SKIP = 32

/** Guacamole-style ordered frame queue: blits run strictly in seq order. */
class OrderedFrameRenderer {
  private nextSeq = 0
  private pending = new Map<number, FrameJob>()
  private draining = false
  private gapTimer: ReturnType<typeof setTimeout> | null = null

  constructor(
    private getCanvas: () => HTMLCanvasElement | null,
    private ensureCanvasSize: (w: number, h: number) => void,
    private markConnected: () => void,
  ) {}

  reset() {
    this.clearGapTimer()
    this.nextSeq = 0
    this.pending.clear()
    this.draining = false
  }

  enqueue(meta: FrameMeta, payload: Uint8Array) {
    if (meta.seq === undefined) return
    this.pending.set(meta.seq, { meta, payload })

    if (
      meta.seq > this.nextSeq &&
      !this.pending.has(this.nextSeq) &&
      meta.seq - this.nextSeq >= MAX_GAP_SKIP
    ) {
      this.skipGapTo(this.lowestPendingSeq())
    }

    void this.drain()
    this.scheduleGapRecoveryIfNeeded()
  }

  private lowestPendingSeq(): number {
    let min = Infinity
    for (const seq of this.pending.keys()) {
      if (seq < min) min = seq
    }
    return min
  }

  private clearGapTimer() {
    if (this.gapTimer !== null) {
      clearTimeout(this.gapTimer)
      this.gapTimer = null
    }
  }

  private scheduleGapRecoveryIfNeeded() {
    if (this.pending.has(this.nextSeq) || this.pending.size === 0) {
      this.clearGapTimer()
      return
    }
    if (this.gapTimer !== null) return

    this.gapTimer = setTimeout(() => {
      this.gapTimer = null
      this.recoverFromGap()
    }, GAP_SKIP_MS)
  }

  /** Advance past lost frames; may leave a briefly stale region until the next update. */
  private skipGapTo(targetSeq: number) {
    if (!Number.isFinite(targetSeq) || targetSeq <= this.nextSeq) return
    const skipped = targetSeq - this.nextSeq
    for (const seq of [...this.pending.keys()]) {
      if (seq < targetSeq) this.pending.delete(seq)
    }
    console.warn(`RDP frame gap: skipped ${skipped} frame(s) (${this.nextSeq} → ${targetSeq})`)
    this.nextSeq = targetSeq
    this.clearGapTimer()
  }

  private recoverFromGap() {
    if (this.pending.has(this.nextSeq) || this.pending.size === 0) return
    const target = this.lowestPendingSeq()
    if (!Number.isFinite(target) || target <= this.nextSeq) return
    this.skipGapTo(target)
    void this.drain()
  }

  private async drain() {
    if (this.draining) return
    this.draining = true
    try {
      while (true) {
        const job = this.pending.get(this.nextSeq)
        if (!job) break
        this.pending.delete(this.nextSeq)
        try {
          await this.blitOrdered(job.meta, job.payload)
        } catch {
          // Corrupt or undecodable frame — advance rather than stall the queue.
        }
        this.nextSeq++
        this.clearGapTimer()
      }
    } finally {
      this.draining = false
      if (this.pending.has(this.nextSeq)) {
        void this.drain()
      } else {
        this.scheduleGapRecoveryIfNeeded()
      }
    }
  }

  private async blitOrdered(meta: FrameMeta, payload: Uint8Array): Promise<void> {
    const canvas = this.getCanvas()
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const ox = meta.x ?? 0
    const oy = meta.y ?? 0

    if (meta.fmt === 'jpeg' || meta.fmt === 'webp') {
      if (ox === 0 && oy === 0 && meta.w && meta.h) {
        this.ensureCanvasSize(meta.w, meta.h)
      }
      await new Promise<void>((resolve, reject) => {
        const bytes = payload.slice()
        const blob = new Blob([bytes], { type: `image/${meta.fmt}` })
        const url = URL.createObjectURL(blob)
        const img = new Image()
        img.onload = () => {
          ctx.drawImage(img, ox, oy)
          URL.revokeObjectURL(url)
          this.markConnected()
          resolve()
        }
        img.onerror = () => {
          URL.revokeObjectURL(url)
          reject(new Error('image decode failed'))
        }
        img.src = url
      })
      return
    }

    if (meta.fmt === 'rgba' && meta.w && meta.h) {
      blitRgba(ctx, meta, payload)
      this.markConnected()
      return
    }

    if (meta.fmt === 'bgra' && meta.w && meta.h) {
      blitBgra(ctx, meta, payload)
      this.markConnected()
      return
    }

    if (meta.fmt === 'h264') {
      this.markConnected()
    }
  }
}

interface CredentialPrompt {
  prompt_id: string
  prompts: Array<{ label: string; echo: boolean }>
}

// PC scan set 1 codes from KeyboardEvent.code (extended keys flagged separately).
const SCAN_CODES: Record<string, number> = {
  Escape: 0x01, Digit1: 0x02, Digit2: 0x03, Digit3: 0x04, Digit4: 0x05,
  Digit5: 0x06, Digit6: 0x07, Digit7: 0x08, Digit8: 0x09, Digit9: 0x0a,
  Digit0: 0x0b, Minus: 0x0c, Equal: 0x0d, Backspace: 0x0e, Tab: 0x0f,
  KeyQ: 0x10, KeyW: 0x11, KeyE: 0x12, KeyR: 0x13, KeyT: 0x14, KeyY: 0x15,
  KeyU: 0x16, KeyI: 0x17, KeyO: 0x18, KeyP: 0x19, BracketLeft: 0x1a,
  BracketRight: 0x1b, Enter: 0x1c, ControlLeft: 0x1d, KeyA: 0x1e, KeyS: 0x1f,
  KeyD: 0x20, KeyF: 0x21, KeyG: 0x22, KeyH: 0x23, KeyJ: 0x24, KeyK: 0x25,
  KeyL: 0x26, Semicolon: 0x27, Quote: 0x28, Backquote: 0x29, ShiftLeft: 0x2a,
  Backslash: 0x2b, KeyZ: 0x2c, KeyX: 0x2d, KeyC: 0x2e, KeyV: 0x2f, KeyB: 0x30,
  KeyN: 0x31, KeyM: 0x32, Comma: 0x33, Period: 0x34, Slash: 0x35,
  ShiftRight: 0x36, NumpadMultiply: 0x37, AltLeft: 0x38, Space: 0x39,
  CapsLock: 0x3a, F1: 0x3b, F2: 0x3c, F3: 0x3d, F4: 0x3e, F5: 0x3f,
  F6: 0x40, F7: 0x41, F8: 0x42, F9: 0x43, F10: 0x44, NumLock: 0x45,
  ScrollLock: 0x46, Numpad7: 0x47, Numpad8: 0x48, Numpad9: 0x49,
  NumpadSubtract: 0x4a, Numpad4: 0x4b, Numpad5: 0x4c, Numpad6: 0x4d,
  NumpadAdd: 0x4e, Numpad1: 0x4f, Numpad2: 0x50, Numpad3: 0x51, Numpad0: 0x52,
  NumpadDecimal: 0x53, F11: 0x57, F12: 0x58,
  ControlRight: 0x1d, AltRight: 0x38, MetaLeft: 0x5b, MetaRight: 0x5c,
  ContextMenu: 0x5d,
  Insert: 0x52, Delete: 0x53, Home: 0x47, End: 0x4f, PageUp: 0x49, PageDown: 0x51,
  ArrowLeft: 0x4b, ArrowUp: 0x48, ArrowRight: 0x4d, ArrowDown: 0x50,
}

const EXTENDED_SCAN_CODES = new Set([
  'Insert', 'Delete', 'Home', 'End', 'PageUp', 'PageDown',
  'ArrowLeft', 'ArrowUp', 'ArrowRight', 'ArrowDown',
  'ControlRight', 'AltRight', 'MetaLeft', 'MetaRight', 'ContextMenu',
  'NumpadDivide', 'NumpadEnter',
])

function scanFromKeyboardEvent(e: KeyboardEvent): { scancode: number; extended: boolean } | null {
  const scancode = SCAN_CODES[e.code]
  if (scancode === undefined) return null
  return { scancode, extended: EXTENDED_SCAN_CODES.has(e.code) }
}

function canvasCoords(canvas: HTMLCanvasElement, e: PointerEvent) {
  const rect = canvas.getBoundingClientRect()
  const scaleX = canvas.width / rect.width
  const scaleY = canvas.height / rect.height
  let x = Math.round((e.clientX - rect.left) * scaleX)
  let y = Math.round((e.clientY - rect.top) * scaleY)
  x = Math.max(0, Math.min(canvas.width - 1, x))
  y = Math.max(0, Math.min(canvas.height - 1, y))
  return { x, y }
}

function blitRgba(
  ctx: CanvasRenderingContext2D,
  meta: FrameMeta,
  payload: Uint8Array,
) {
  if (!meta.w || !meta.h) return
  const imageData = ctx.createImageData(meta.w, meta.h)
  const dst = imageData.data
  const len = Math.min(payload.length, dst.length)
  dst.set(payload.subarray(0, len))
  // IronRDP may leave alpha at 0; force opaque so pixels are visible on black bg.
  for (let i = 3; i < len; i += 4) {
    dst[i] = 255
  }
  ctx.putImageData(imageData, meta.x ?? 0, meta.y ?? 0)
}

function blitBgra(
  ctx: CanvasRenderingContext2D,
  meta: FrameMeta,
  payload: Uint8Array,
) {
  if (!meta.w || !meta.h) return
  const imageData = ctx.createImageData(meta.w, meta.h)
  const dst = imageData.data
  const len = Math.min(payload.length, dst.length)
  for (let i = 0; i < len; i += 4) {
    dst[i] = payload[i + 2]
    dst[i + 1] = payload[i + 1]
    dst[i + 2] = payload[i]
    dst[i + 3] = 255
  }
  ctx.putImageData(imageData, meta.x ?? 0, meta.y ?? 0)
}

export default function RDPViewer() {
  const { resourceId } = useParams<{ resourceId: string }>()
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [status, setStatus] = useState<Status>('connecting')
  // U4: the server's actual termination/error message (e.g. "The connector
  // for this resource is at capacity. Please try again shortly.") -- the
  // status pill alone only ever showed the generic word "denied" or
  // "disconnected", which read identically whether the cause was a policy
  // deny, a capacity rejection, or an unrelated failure.
  const [statusMessage, setStatusMessage] = useState<string | null>(null)
  const [clipboardAllowed, setClipboardAllowed] = useState(false)
  const [credPrompt, setCredPrompt] = useState<CredentialPrompt | null>(null)
  const [credInputs, setCredInputs] = useState<string[]>([])
  const wsRef = useRef<WebSocket | null>(null)
  const credActiveRef = useRef(false)
  const desktopSizeRef = useRef({ w: 0, h: 0 })
  const connectedOnceRef = useRef(false)
  const frameRendererRef = useRef<OrderedFrameRenderer | null>(null)

  const markConnected = useCallback(() => {
    if (!connectedOnceRef.current) {
      connectedOnceRef.current = true
      setStatus('connected')
    }
  }, [])

  const ensureCanvasSize = useCallback((w: number, h: number) => {
    const canvas = canvasRef.current
    if (!canvas || w <= 0 || h <= 0) return
    desktopSizeRef.current = { w, h }
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w
      canvas.height = h
    }
  }, [])

  useEffect(() => {
    frameRendererRef.current = new OrderedFrameRenderer(
      () => canvasRef.current,
      ensureCanvasSize,
      markConnected,
    )
    return () => {
      frameRendererRef.current?.reset()
      frameRendererRef.current = null
    }
  }, [ensureCanvasSize, markConnected])

  const enqueueFrame = useCallback((meta: FrameMeta, payload: Uint8Array) => {
    frameRendererRef.current?.enqueue(meta, payload)
  }, [])

  const handleDisconnect = useCallback(() => {
    const ws = wsRef.current
    if (ws) {
      if (ws.readyState === WebSocket.OPEN) {
        try {
          ws.send(encodeJSON(MSG_CONTROL, { event: 'disconnect', message: 'user disconnect' }))
        } catch { /* ignore */ }
        ws.close(1000, 'user disconnect')
      } else if (ws.readyState === WebSocket.CONNECTING) {
        ws.close()
      }
    }
    wsRef.current = null
    credActiveRef.current = false
    connectedOnceRef.current = false
    setCredPrompt(null)
    setCredInputs([])
    setStatus('disconnected')
  }, [])

  const handleCredSubmit = useCallback(() => {
    if (!credPrompt || !wsRef.current) return
    wsRef.current.send(encodeJSON(MSG_CRED_REPLY, {
      prompt_id: credPrompt.prompt_id,
      responses: credInputs,
    }))
    setCredInputs((prev) => prev.map(() => ''))
    credActiveRef.current = false
    setCredPrompt(null)
  }, [credPrompt, credInputs])

  useEffect(() => {
    if (!canvasRef.current || !resourceId) return

    connectedOnceRef.current = false
    desktopSizeRef.current = { w: 0, h: 0 }
    frameRendererRef.current?.reset()

    const ws = new WebSocket(
      `wss://${window.location.host}/session/rdp?resource_id=${resourceId}`,
    )
    ws.binaryType = 'arraybuffer'
    wsRef.current = ws

    const sendInput = (obj: object) => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(encodeJSON(MSG_DATA, obj))
      }
    }

    ws.onopen = () => setStatus('connecting')

    ws.onmessage = (event) => {
      const data = new Uint8Array(event.data as ArrayBuffer)
      if (data.byteLength < 6) return
      const type = data[5]
      const payload = data.slice(6)
      const dec = new TextDecoder()

      switch (type) {
        case MSG_DATA: {
          const sep = payload.indexOf(0)
          if (sep < 0) return
          try {
            const meta = JSON.parse(dec.decode(payload.subarray(0, sep))) as FrameMeta
            enqueueFrame(meta, payload.subarray(sep + 1))
          } catch { /* ignore */ }
          break
        }
        case MSG_CRED_PROMPT: {
          try {
            const prompt: CredentialPrompt = JSON.parse(dec.decode(payload))
            setStatus('connecting')
            credActiveRef.current = true
            setCredPrompt(prompt)
            setCredInputs(new Array(prompt.prompts.length).fill(''))
          } catch { /* ignore */ }
          break
        }
        case MSG_CONTROL: {
          try {
            const ctrl = JSON.parse(dec.decode(payload))
            if (ctrl.event === 'clipboard_allowed') {
              setClipboardAllowed(true)
            }
            if (ctrl.event === 'rdp_connected') {
              markConnected()
              try {
                const dims = JSON.parse(ctrl.message ?? '{}') as { w?: number; h?: number }
                if (dims.w && dims.h) {
                  ensureCanvasSize(dims.w, dims.h)
                }
              } catch { /* ignore */ }
              canvasRef.current?.focus()
            }
            if (ctrl.event === 'session_terminated' || ctrl.event === 'error') {
              setStatus(ctrl.event === 'error' ? 'denied' : 'disconnected')
              setStatusMessage(typeof ctrl.message === 'string' ? ctrl.message : null)
            }
          } catch { /* ignore */ }
          break
        }
        default:
          break
      }
    }

    ws.onclose = () => setStatus('disconnected')

    const onKeyDown = (e: KeyboardEvent) => {
      if (credActiveRef.current) return
      const scan = scanFromKeyboardEvent(e)
      if (!scan) return
      e.preventDefault()
      sendInput({ op: 'key', scancode: scan.scancode, extended: scan.extended, down: true })
    }
    const onKeyUp = (e: KeyboardEvent) => {
      if (credActiveRef.current) return
      const scan = scanFromKeyboardEvent(e)
      if (!scan) return
      e.preventDefault()
      sendInput({ op: 'key', scancode: scan.scancode, extended: scan.extended, down: false })
    }
    const sendMouse = (e: PointerEvent, down: boolean) => {
      if (credActiveRef.current) return
      const canvas = canvasRef.current
      if (!canvas) return
      const { x, y } = canvasCoords(canvas, e)
      sendInput({ op: 'mouse', x, y, btn: e.button, down })
    }
    let lastMoveSent = 0
    const onPointerMove = (e: PointerEvent) => {
      if (credActiveRef.current) return
      const now = performance.now()
      if (now - lastMoveSent < 33) return
      lastMoveSent = now
      const canvas = canvasRef.current
      if (!canvas) return
      const { x, y } = canvasCoords(canvas, e)
      sendInput({ op: 'mousemove', x, y })
    }
    const onPointerDown = (e: PointerEvent) => {
      e.preventDefault()
      const canvas = canvasRef.current
      if (!canvas) return
      canvas.focus()
      canvas.setPointerCapture(e.pointerId)
      sendMouse(e, true)
    }
    const onPointerUp = (e: PointerEvent) => {
      e.preventDefault()
      const canvas = canvasRef.current
      if (canvas?.hasPointerCapture(e.pointerId)) {
        canvas.releasePointerCapture(e.pointerId)
      }
      sendMouse(e, false)
    }

    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('keyup', onKeyUp)
    const canvas = canvasRef.current
    canvas.addEventListener('pointermove', onPointerMove)
    canvas.addEventListener('pointerdown', onPointerDown)
    canvas.addEventListener('pointerup', onPointerUp)
    canvas.addEventListener('pointercancel', onPointerUp)

    return () => {
      ws.close()
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('keyup', onKeyUp)
      canvas.removeEventListener('pointermove', onPointerMove)
      canvas.removeEventListener('pointerdown', onPointerDown)
      canvas.removeEventListener('pointerup', onPointerUp)
      canvas.removeEventListener('pointercancel', onPointerUp)
    }
  }, [resourceId, enqueueFrame, ensureCanvasSize, markConnected])

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center gap-3 border-b border-border bg-surface px-4 py-2 text-sm">
        <span
          className={`inline-block h-2 w-2 rounded-full ${
            status === 'connected'
              ? 'bg-success animate-pulse'
              : status === 'connecting'
                ? 'bg-warning animate-pulse'
                : status === 'denied'
                  ? 'bg-danger'
                  : 'bg-muted'
          }`}
        />
        <span className="capitalize text-foreground">{status}</span>
        {statusMessage && (status === 'denied' || status === 'disconnected') && (
          <span className="text-muted">— {statusMessage}</span>
        )}
        <span className="text-muted">RDP — {resourceId}</span>
        <div className="ml-auto flex items-center gap-2">
          {clipboardAllowed && (
            <span className="rounded-md border border-success/20 bg-success-muted px-2 py-0.5 text-xs text-success-foreground">
              clipboard on
            </span>
          )}
          {(status === 'connected' || status === 'connecting') && (
            <button
              type="button"
              onClick={handleDisconnect}
              className="rounded-lg border border-danger/40 bg-danger-muted px-3 py-1 text-xs font-medium text-danger-foreground transition-colors hover:bg-danger/20"
            >
              Disconnect
            </button>
          )}
          {status === 'disconnected' && (
            <Link
              to="/catalog"
              className="rounded-lg border border-border px-3 py-1 text-xs font-medium text-primary transition-colors hover:bg-surface-muted"
            >
              Back to Resources
            </Link>
          )}
          <span className="text-xs text-muted">IronRDP server-side (ADR-008)</span>
        </div>
      </div>

      {credPrompt && (
        <div className="border-b border-border bg-surface px-4 py-3">
          <form
            className="flex flex-wrap items-end gap-3"
            onSubmit={(e) => {
              e.preventDefault()
              handleCredSubmit()
            }}
          >
            {credPrompt.prompts.map((field, idx) => (
              <label key={field.label} className="flex flex-col gap-1 text-xs text-muted">
                {field.label}
                <input
                  type={field.echo ? 'text' : 'password'}
                  className="rounded-lg border border-border bg-surface px-2 py-1 text-sm text-foreground"
                  value={credInputs[idx] ?? ''}
                  onChange={(e) => {
                    const next = [...credInputs]
                    next[idx] = e.target.value
                    setCredInputs(next)
                  }}
                  autoFocus={idx === 0}
                />
              </label>
            ))}
            <button
              type="submit"
              className="rounded-lg bg-primary px-3 py-1 text-xs font-medium text-primary-foreground hover:bg-primary-hover"
            >
              Connect
            </button>
          </form>
        </div>
      )}

      {/* Canvas mount wrapper — keep flex centering; do not alter canvas box model */}
      <div className="flex flex-1 items-center justify-center bg-black">
        <canvas
          ref={canvasRef}
          width={1280}
          height={720}
          className="max-h-full max-w-full cursor-default touch-none outline-none"
          tabIndex={0}
          onContextMenu={(e) => e.preventDefault()}
        />
      </div>
    </div>
  )
}
